import React, { useState, useRef, useEffect } from 'react';
import { UserPlus, ChevronDown, Check } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { OrgMember } from '../../hooks/useOrgMembers';

interface AssignDropdownProps {
  table: 'candidates' | 'missions';
  rowId: string | number;
  assignedTo: string | null;
  members: OrgMember[];
  onAssigned?: (userId: string | null) => void;
  align?: 'left' | 'right';
}

const getInitials = (name: string) =>
  name.split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase();

const MemberAvatar: React.FC<{ member: OrgMember; size?: 'sm' | 'xs' }> = ({ member, size = 'sm' }) => {
  const dim = size === 'xs' ? 'w-5 h-5 text-[9px]' : 'w-6 h-6 text-[10px]';
  return (
    <span className={`${dim} rounded-full bg-amber-500 text-white font-bold flex items-center justify-center shrink-0`}>
      {getInitials(member.full_name)}
    </span>
  );
};

const AssignDropdown: React.FC<AssignDropdownProps> = ({ table, rowId, assignedTo, members, onAssigned, align = 'right' }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const assignedMember = members.find(m => m.user_id === assignedTo);

  const handleSelect = async (userId: string | null) => {
    setLoading(true);
    setOpen(false);
    const { error } = await supabase
      .from(table)
      .update({ assigned_to: userId } as any)
      .eq('id', rowId as any);

    setLoading(false);
    if (!error) onAssigned?.(userId);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        disabled={loading}
        className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs border transition-all ${
          assignedMember
            ? 'border-amber-200 bg-amber-50 text-amber-700 hover:border-amber-400'
            : 'border-slate-200 bg-white text-slate-500 hover:border-amber-300 hover:text-amber-600'
        }`}
        title="Assigner à un BM"
      >
        {loading ? (
          <span className="text-slate-400">…</span>
        ) : assignedMember ? (
          <>
            <MemberAvatar member={assignedMember} size="xs" />
            <span className="max-w-[80px] truncate font-medium">{assignedMember.full_name.split(' ')[0]}</span>
          </>
        ) : (
          <>
            <UserPlus size={12} />
            <span>Assigner</span>
          </>
        )}
        <ChevronDown size={10} className="text-slate-400 shrink-0" />
      </button>

      {open && (
        <div className={`absolute z-50 top-full mt-1 ${align === 'right' ? 'right-0' : 'left-0'} min-w-[200px] bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden`}>
          <div className="px-3 py-2 border-b border-slate-100">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Assigner à</p>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); handleSelect(null); }}
            className="w-full flex items-center justify-between px-3 py-2 text-xs text-slate-500 hover:bg-slate-50"
          >
            <span className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full border-2 border-dashed border-slate-300 flex items-center justify-center">
                <UserPlus size={10} className="text-slate-400" />
              </span>
              Non assigné
            </span>
            {!assignedTo && <Check size={11} className="text-amber-500" />}
          </button>
          <div className="border-t border-slate-100" />
          {members.map(m => (
            <button
              key={m.user_id}
              onClick={(e) => { e.stopPropagation(); handleSelect(m.user_id); }}
              className="w-full flex items-center justify-between px-3 py-2 text-xs text-slate-700 hover:bg-amber-50 transition-colors"
            >
              <span className="flex items-center gap-2 min-w-0">
                <MemberAvatar member={m} />
                <span className="min-w-0">
                  <span className="font-semibold block truncate max-w-[130px]">{m.full_name}</span>
                  <span className="text-slate-400 capitalize">{m.role}</span>
                </span>
              </span>
              {assignedTo === m.user_id && <Check size={11} className="text-amber-500 shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AssignDropdown;
